/**
 * Microphone Model Types
 *
 * Each MicKey value maps 1:1 to a config key in
 * `constants.equipment.microphones`. State stores this key directly —
 * no string transformation needed to reach the config.
 */

/**
 * Union of all valid microphone config keys.
 */
export type MicKey =
  | 'tcm_x'       // Parlé TCM-X  — ceiling, standard
  | 'dcm_x'       // Parlé DCM-X  — ceiling EXPANSION mic (Devio UCX); same specs as TCM-X
  | 'tcm_1'       // Parlé TCM-1  — ceiling, pendant
  | 'ttm_x'       // Parlé TTM-X  — tabletop
  | 'vbc_2500a'   // Parlé VBC 2500a — video bar (wall-mount, camera)
  | 'vbc_2800'    // Parlé VBC 2800  — video bar (wall-mount, camera)
  | 'abc_2500a'   // Parlé ABC 2500a — audio bar (wall-mount, no camera)
  | 'cbc_2500_cm' // Parlé CBC 2500 CM — ceiling bar, cable-mount, camera
  | 'cbc_2500_pm' // Parlé CBC 2500 PM — ceiling bar, pole-mount, no camera
  | 'bma360d_24'  // BMA360D 24" — ceiling tile mic, imperial (0.6096m × 0.6096m tile)
  | 'bma360d_600'; // BMA360D 600mm — ceiling tile mic, metric (0.6m × 0.6m tile)

/**
 * Categorical lookup sets for microphone feature detection.
 *
 * Replace all boolean-flag reads on config objects with set membership checks:
 *
 *   // Before:
 *   const isBarMic = micConfig?.isBarMic ?? false;
 *
 *   // After:
 *   const isBarMic = MIC_CATEGORIES.BAR.has(micKey);
 */
export const MIC_CATEGORIES = {
  /**
   * Mics that mount from the ceiling (NOT including pendant — handled separately).
   * Use to enter the ceiling-height branch in setMicrophones.
   */
  CEILING_MOUNTED: new Set<MicKey>(['tcm_x', 'dcm_x', 'cbc_2500_cm', 'cbc_2500_pm', 'bma360d_24', 'bma360d_600']),

  /**
   * Ceiling tile mics (BMA360D variants). Sit flush in the ceiling tile at ceiling height.
   * Height is fixed to ceilingHeight.min — not user-adjustable.
   */
  CEILING_TILE: new Set<MicKey>(['bma360d_24', 'bma360d_600']),

  /** Mics with an integrated or tightly paired camera. */
  HAS_CAMERA: new Set<MicKey>(['vbc_2500a', 'vbc_2800']),

  /** Bar form-factor mics (wall or ceiling bars). */
  BAR: new Set<MicKey>(['vbc_2500a', 'vbc_2800', 'abc_2500a', 'cbc_2500_cm', 'cbc_2500_pm']),

  /**
   * Mics with a fixed mounting height that the user cannot adjust.
   * Excludes CBC bars (ceiling-mounted with adjustable height).
   */
  FIXED_HEIGHT: new Set<MicKey>(['vbc_2500a', 'vbc_2800', 'abc_2500a', 'ttm_x']),

  /** Pendant-style ceiling mics (hang by cable, continuous height adjustment). */
  PENDANT: new Set<MicKey>(['tcm_1']),

  /** Tabletop mics. */
  TABLETOP: new Set<MicKey>(['ttm_x']),

  /** CBC series ceiling-bar mics (CM cable-mount and PM pole-mount). */
  CBC: new Set<MicKey>(['cbc_2500_cm', 'cbc_2500_pm']),

  /**
   * Mics that snap height to 0.5-foot pole increments.
   * = CEILING_MOUNTED minus CBC CM (cable mount — continuous).
   */
  POLE_MOUNT: new Set<MicKey>(['tcm_x', 'dcm_x', 'cbc_2500_pm']),

  /** Wall-mounted bar mics (snap to nearest wall on manual drag). */
  WALL_MOUNTED: new Set<MicKey>(['vbc_2500a', 'vbc_2800', 'abc_2500a']),
} as const;

/**
 * Type-safe set-membership helper.
 *
 * @example
 *   micInCategory(area.microphones.model, 'BAR')   // → true for vbc_2500a etc.
 *   micInCategory(area.microphones.model, 'PENDANT') // → true for tcm_1 only
 */
export function micInCategory(
  key: string | null | undefined,
  category: keyof typeof MIC_CATEGORIES,
): boolean {
  if (!key) return false;
  return (MIC_CATEGORIES[category] as Set<string>).has(key);
}

/**
 * Primary microphone type, per the taxonomy spec §2.1's "different primary
 * microphone type" refers to: Bar, Ceiling, Ceiling Tile, Pendant, Table.
 * Distinct from `expansionMicType` ('ceiling' | 'table'), which is a
 * different field on a Bar-primary design and never changes this.
 */
export type PrimaryMicType = 'bar' | 'ceiling' | 'ceilingTile' | 'pendant' | 'table' | null;

/**
 * Classifies a primary microphone model into its top-level type. BAR is
 * checked before CEILING_MOUNTED — the CBC series (ceiling-mounted bars) is
 * in both sets, and a CBC model is a Bar, not a Ceiling, primary type.
 */
export function getPrimaryMicType(model: string | null | undefined): PrimaryMicType {
  if (!model) return null;
  if (MIC_CATEGORIES.BAR.has(model as MicKey)) return 'bar';
  if (MIC_CATEGORIES.CEILING_TILE.has(model as MicKey)) return 'ceilingTile';
  if (MIC_CATEGORIES.PENDANT.has(model as MicKey)) return 'pendant';
  if (MIC_CATEGORIES.TABLETOP.has(model as MicKey)) return 'table';
  if (MIC_CATEGORIES.CEILING_MOUNTED.has(model as MicKey)) return 'ceiling';
  return null;
}
